import { Component, Inject, OnInit, OnDestroy } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { openShareLinkDialog } from 'src/app/shared/components/share-link-dialog/share-link-dialog.composable';
import { ContractVaultHttpService } from 'src/app/shared/services/v1/contract-vault/contract-vault.http.service';
import { apiUrl } from 'src/environments/environment';

/**
 * Data contract for opening the Global File Preview Dialog.
 */
export interface IFilePreviewDialogData {
  /** Database ID of the artifact if available for sharing. */
  artifactId?: number;
  /** Target file inline stream / preview URL. */
  viewUrl?: string;
  /** Target file attachment download URL. */
  downloadUrl?: string;
  /** Fallback URL if viewUrl/downloadUrl are not differentiated. */
  fileUrl?: string;
  /** Display name of the file (e.g. "Client_Contract_Signed.pdf"). */
  fileName: string;
  /** Optional MIME type (e.g. "application/pdf", "image/png"). */
  mimeType?: string;
  /** Optional file size in bytes for header display. */
  fileSizeBytes?: number;
  /** Optional document category label (e.g. "Contract", "Rider", "W-9"). */
  category?: string;
}

/**
 * Global Full-Screen File & Document Previewer Dialog Component.
 * Supports rich, near full-screen in-browser rendering for PDF documents, images, and text.
 */
@Component({
  selector: 'app-file-preview-dialog',
  templateUrl: './file-preview-dialog.component.html',
  styleUrls: ['./file-preview-dialog.component.scss']
})
export class FilePreviewDialogComponent implements OnInit, OnDestroy {
  public resolvedViewUrl: string = '';
  public resolvedDownloadUrl: string = '';
  public safeFileUrl: SafeResourceUrl | null = null;
  public fileName: string = 'Document';
  public category: string = '';
  public fileSizeBytes?: number;
  public isImage: boolean = false;
  public isPdf: boolean = true;
  public isLoading: boolean = true;
  public hasError: boolean = false;

  private createdBlobUrl?: string;
  private watchdogTimer?: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IFilePreviewDialogData,
    private dialogRef: MatDialogRef<FilePreviewDialogComponent>,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private contractVaultHttp: ContractVaultHttpService
  ) {}

  ngOnInit(): void {
    if (this.data) {
      this.fileName = this.data.fileName || 'Document';
      this.category = this.data.category || '';
      this.fileSizeBytes = this.data.fileSizeBytes;

      // Determine file format type
      const lowerName = this.fileName.toLowerCase();
      this.isImage = lowerName.endsWith('.png') || lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg') || lowerName.endsWith('.webp') || lowerName.endsWith('.gif');
      this.isPdf = lowerName.endsWith('.pdf') || (this.data.mimeType === 'application/pdf');

      // Resolve clean semantic URLs
      const rawView = this.data.viewUrl || this.data.fileUrl || '';
      const rawDownload = this.data.downloadUrl || this.data.fileUrl || rawView;

      this.resolvedViewUrl = this.resolveAbsoluteUrl(rawView);
      this.resolvedDownloadUrl = this.resolveAbsoluteUrl(rawDownload);

      if (this.resolvedViewUrl) {
        this.isLoading = true;
        this.contractVaultHttp.downloadFileBlob(this.resolvedViewUrl).subscribe({
          next: (blob) => {
            this.createdBlobUrl = window.URL.createObjectURL(blob);
            this.safeFileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.createdBlobUrl);
            this.isLoading = false;
            this.hasError = false;
          },
          error: () => {
            // Fallback to direct sanitized URL
            this.safeFileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.resolvedViewUrl);
            this.startWatchdog();
          }
        });
      }
    }
  }

  ngOnDestroy(): void {
    if (this.watchdogTimer) clearTimeout(this.watchdogTimer);
    if (this.createdBlobUrl) {
      window.URL.revokeObjectURL(this.createdBlobUrl);
    }
  }

  private startWatchdog(): void {
    if (this.watchdogTimer) clearTimeout(this.watchdogTimer);
    this.watchdogTimer = setTimeout(() => {
      if (this.isLoading && !this.hasError) {
        this.isLoading = false;
      }
    }, 4000);
  }

  /**
   * Handles successful iframe / image load event.
   */
  public onLoad(): void {
    if (this.watchdogTimer) clearTimeout(this.watchdogTimer);
    this.isLoading = false;
    this.hasError = false;
  }

  /**
   * Handles iframe load error event.
   */
  public onError(): void {
    this.isLoading = false;
    this.hasError = true;
  }


  /**
   * Triggers file attachment download via authenticated HTTP client.
   */
  public downloadFile(): void {
    if (this.resolvedDownloadUrl) {
      this.contractVaultHttp.downloadFileBlob(this.resolvedDownloadUrl).subscribe({
        next: (blob) => {
          this.contractVaultHttp.saveBlobAsFile(blob, this.fileName || 'document.pdf');
        },
        error: () => {
          // Fallback to direct tab opening if needed
          window.open(this.resolvedDownloadUrl, '_blank');
        }
      });
    }
  }


  /**
   * Triggers navigation in a dedicated new browser tab (Inline View mode).
   */
  public openInNewTab(): void {
    if (this.resolvedViewUrl) {
      window.open(this.resolvedViewUrl, '_blank');
    }
  }

  /**
   * Opens the share link configuration dialog.
   */
  public shareFile(): void {
    if (this.data?.artifactId) {
      openShareLinkDialog(this.dialog, {
        artifactId: this.data.artifactId,
        fileName: this.fileName,
        category: this.category
      });
    }
  }


  /**
   * Resolves relative path against host API URL.
   */
  private resolveAbsoluteUrl(url?: string): string {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (url.startsWith('/')) {
      const host = apiUrl.replace(/\/api(\/v\d+)?\/?$/i, '');
      return `${host}${url}`;
    }
    return url;
  }

  /**
   * Formats raw bytes to human-readable size string.
   */
  public formatFileSize(bytes?: number): string {
    if (!bytes || bytes === 0) return '';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /**
   * Closes the preview modal.
   */
  public close(): void {
    this.dialogRef.close();
  }
}
